import React, { useMemo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

// US federal holidays (date strings YYYY-MM-DD). We hardcode the next ~24 months
// so we don't depend on a date library. List covers 2026-2027.
const US_FEDERAL_HOLIDAYS = new Set([
  // 2026
  "2026-01-01", // New Year's Day
  "2026-01-19", // MLK Day
  "2026-02-16", // Presidents' Day
  "2026-05-25", // Memorial Day
  "2026-06-19", // Juneteenth
  "2026-07-03", // July 4th observed (Saturday) — most companies close Friday
  "2026-07-04", // Independence Day
  "2026-09-07", // Labor Day
  "2026-10-12", // Columbus Day
  "2026-11-11", // Veterans Day
  "2026-11-26", // Thanksgiving
  "2026-11-27", // Day after Thanksgiving
  "2026-12-24", // Christmas Eve
  "2026-12-25", // Christmas
  "2026-12-31", // NY Eve
  // 2027
  "2027-01-01",
  "2027-01-18",
  "2027-02-15",
  "2027-05-31",
  "2027-06-19",
  "2027-07-05", // 4th observed (Sunday)
  "2027-09-06",
  "2027-10-11",
  "2027-11-11",
  "2027-11-25",
  "2027-11-26",
  "2027-12-24",
  "2027-12-25",
  "2027-12-31",
]);

// Business hours — match Velcor's office hours (footer):
// Mon-Fri: 9:00 AM – 6:30 PM
// Saturday: 9:00 AM – 2:00 PM
// Sunday: closed
const CUTOFFS = {
  // Day of week index from getDay(): Sun=0, Mon=1, ... Sat=6
  1: 18 * 60 + 30, // Mon → 6:30 PM
  2: 18 * 60 + 30, // Tue
  3: 18 * 60 + 30, // Wed
  4: 18 * 60 + 30, // Thu
  5: 18 * 60 + 30, // Fri
  6: 14 * 60,      // Sat → 2:00 PM
};

const fmtIsoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isHoliday = (d) => US_FEDERAL_HOLIDAYS.has(fmtIsoDate(d));
const isSunday = (d) => d.getDay() === 0;

const isBusinessDay = (d) => !isSunday(d) && !isHoliday(d);

/**
 * Earliest date a customer can pick (today's "next business day" rule).
 * - If today is a business day AND the current time is before today's cutoff → today is selectable.
 * - Otherwise → roll forward to the next business day.
 */
function earliestSelectableDate(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (isBusinessDay(today)) {
    const minutes = now.getHours() * 60 + now.getMinutes();
    const cutoff = CUTOFFS[today.getDay()];
    if (cutoff && minutes < cutoff) return today;
  }
  // Walk forward
  const cursor = new Date(today);
  cursor.setDate(cursor.getDate() + 1);
  while (!isBusinessDay(cursor)) {
    cursor.setDate(cursor.getDate() + 1);
  }
  return cursor;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const monthName = (d, lang) =>
  d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "long", year: "numeric" });
const weekdayShort = (idx, lang) => {
  // idx 0 = Sunday … 6 = Saturday (US standard)
  const baseSun = new Date(2026, 0, 4); // Sunday
  const d = new Date(baseSun);
  d.setDate(baseSun.getDate() + idx);
  return d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "short" });
};

export default function BusinessDayPicker({ value, onChange, label, lang = "es", testid }) {
  const earliest = useMemo(() => earliestSelectableDate(), []);
  const [view, setView] = useState(() => new Date(earliest.getFullYear(), earliest.getMonth(), 1));
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = value ? new Date(value + "T00:00:00") : null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // When opening, check available vertical space and flip up if the popover
  // would clip past the viewport (e.g. when the picker sits near the bottom
  // of the screen — typical on /enroll where this is the last form field).
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const POPOVER_APPROX_HEIGHT = 380; // header + 6 weeks + legend
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setFlipUp(spaceBelow < POPOVER_APPROX_HEIGHT && spaceAbove > spaceBelow);
  }, [open]);

  // Also recompute on window resize while open
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setFlipUp(window.innerHeight - rect.bottom < 380 && rect.top > window.innerHeight - rect.bottom);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  // Build the grid for `view` month, starting on Sunday (US standard)
  const grid = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const offset = first.getDay(); // 0=Sun … 6=Sat — exactly what we need
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    const lastDay = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= lastDay; d++) {
      days.push(new Date(view.getFullYear(), view.getMonth(), d));
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [view]);

  const canPrev = () => {
    const prev = new Date(view.getFullYear(), view.getMonth() - 1, 1);
    const earliestMonth = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
    return prev >= earliestMonth;
  };
  const goPrev = () => canPrev() && setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const goNext = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const isDisabled = (d) => {
    if (!d) return true;
    if (d < earliest) return true;
    if (!isBusinessDay(d)) return true;
    return false;
  };

  const pick = (d) => {
    if (isDisabled(d)) return;
    onChange(fmtIsoDate(d));
    setOpen(false);
  };

  const labelDisplay = selected
    ? selected.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : ""; // empty when nothing picked — only the calendar icon shows

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      {label && (
        <label className="block text-[11.5px] font-black text-slate-700 mb-1.5 uppercase tracking-[0.08em]">
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-12 pl-11 pr-4 rounded-xl border bg-white text-left transition flex items-center ${
          open
            ? "border-velcor-green ring-2 ring-velcor-green/15"
            : "border-slate-200 hover:border-slate-300"
        }`}
        data-testid={`${testid || "biz-picker"}-trigger`}
      >
        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        {selected && (
          <span className="text-[14px] font-semibold capitalize text-velcor-ink">
            {labelDisplay}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-30 w-[286px] sm:w-[296px] left-0 rounded-2xl bg-white border border-slate-200 shadow-[0_28px_70px_rgba(15,23,42,0.18)] p-3.5 ${
            flipUp ? "bottom-full mb-2" : "top-full mt-2"
          }`}
          data-testid={`${testid || "biz-picker"}-popover`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2.5">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev()}
              className="grid place-items-center w-8 h-8 rounded-full text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <div className="font-black text-[13.5px] text-velcor-ink capitalize">
              {monthName(view, lang)}
            </div>
            <button
              type="button"
              onClick={goNext}
              className="grid place-items-center w-8 h-8 rounded-full text-slate-600 hover:bg-slate-100"
              aria-label="Next month"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Weekday header (Sun..Sat — US standard) */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {[0, 1, 2, 3, 4, 5, 6].map((idx) => (
              <div
                key={idx}
                className={`text-center text-[10.5px] font-black uppercase tracking-[0.1em] py-1 ${
                  idx === 0 ? "text-rose-300" : "text-slate-400"
                }`}
              >
                {weekdayShort(idx, lang).replace(".", "").slice(0, 3)}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={`pad-${i}`} className="h-9" />;
              const disabled = isDisabled(d);
              const isSel = selected && sameDay(d, selected);
              const isToday = sameDay(d, new Date());
              const sunday = isSunday(d);
              const holiday = isHoliday(d);
              return (
                <button
                  type="button"
                  key={fmtIsoDate(d)}
                  onClick={() => pick(d)}
                  disabled={disabled}
                  title={
                    holiday
                      ? lang === "es"
                        ? "Día feriado"
                        : "Holiday"
                      : sunday
                        ? lang === "es"
                          ? "Domingo · cerrado"
                          : "Sunday · closed"
                        : disabled
                          ? lang === "es"
                            ? "No disponible"
                            : "Unavailable"
                          : ""
                  }
                  className={`h-9 rounded-lg text-[12.5px] font-bold transition ${
                    isSel
                      ? "bg-velcor-green text-white shadow-[0_8px_18px_rgba(47,122,58,0.35)]"
                      : disabled
                        ? holiday
                          ? "text-rose-300 line-through cursor-not-allowed"
                          : sunday
                            ? "text-slate-300 cursor-not-allowed"
                            : "text-slate-300 cursor-not-allowed"
                        : isToday
                          ? "border border-velcor-green/40 text-velcor-ink hover:bg-velcor-green/8"
                          : "text-velcor-ink hover:bg-slate-100"
                  }`}
                  data-testid={`day-${fmtIsoDate(d)}`}
                  aria-label={fmtIsoDate(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 text-[10.5px] font-extrabold uppercase tracking-[0.06em]">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-velcor-green shrink-0" />
              <span className="text-slate-600">{lang === "es" ? "Disponible" : "Available"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-200 shrink-0" />
              <span className="text-slate-600">{lang === "es" ? "Feriado" : "Holiday"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-200 shrink-0" />
              <span className="text-slate-600">{lang === "es" ? "Cerrado" : "Closed"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
